package es.subastia.savedsearches

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import es.subastia.navigation.NavRoute
import es.subastia.navigation.Navigator
import es.subastia.ui.components.LoginRequiredPlaceholder
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.default_search_title
import subastia.composeapp.generated.resources.desc_delete_search
import subastia.composeapp.generated.resources.empty_saved_searches_title
import subastia.composeapp.generated.resources.login_required_saved_searches
import subastia.composeapp.generated.resources.title_saved_searches

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SavedSearchesScreen(navigator: Navigator) {
    val viewModel: SavedSearchesViewModel = koinViewModel()
    val state by viewModel.uiState.collectAsStateWithLifecycle()

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        stringResource(Res.string.title_saved_searches),
                        style = MaterialTheme.typography.titleLarge,
                    )
                },
            )
        },
    ) { paddingValues ->
        Box(
            Modifier
                .fillMaxSize()
                .padding(paddingValues),
            contentAlignment = Alignment.Center,
        ) {
            when (state) {
                SavedSearchesUiState.Loading -> {
                    CircularProgressIndicator()
                }
                SavedSearchesUiState.NotLoggedIn -> {
                    LoginRequiredPlaceholder(
                        message = stringResource(Res.string.login_required_saved_searches),
                        navigator = navigator,
                    )
                }
                is SavedSearchesUiState.Success -> {
                    val list = (state as SavedSearchesUiState.Success).list
                    if (list.isEmpty()) {
                        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Text(
                                stringResource(Res.string.empty_saved_searches_title),
                                style = MaterialTheme.typography.bodyLarge,
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(16.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp),
                        ) {
                            items((state as SavedSearchesUiState.Success).list) { savedSearch ->
                                Card(
                                    onClick = {
                                        navigator.navigateTo(
                                            NavRoute.AuctionList(
                                                query = savedSearch.title, // TODO: Separate query from title in model to avoid searching "Subastas en..."
                                                type = savedSearch.type?.id,
                                                procedureType = savedSearch.procedureType?.name,
                                                province = savedSearch.province?.nm
                                            )
                                        )
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                ) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(16.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = savedSearch.title ?: stringResource(Res.string.default_search_title),
                                                style = MaterialTheme.typography.titleMedium,
                                                maxLines = 1,
                                                overflow = TextOverflow.Ellipsis,
                                            )

                                            val details = listOfNotNull(
                                                savedSearch.province?.nm,
                                                savedSearch.type?.text,
                                                savedSearch.procedureType?.label,
                                            ).joinToString(", ")

                                            if (details.isNotEmpty()) {
                                                Text(
                                                    text = details,
                                                    style = MaterialTheme.typography.bodyMedium,
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                                    maxLines = 2,
                                                    overflow = TextOverflow.Ellipsis,
                                                )
                                            }
                                        }
                                        IconButton(
                                            onClick = { viewModel.deleteSearch(savedSearch.id) },
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.Delete,
                                                contentDescription = stringResource(Res.string.desc_delete_search),
                                                tint = MaterialTheme.colorScheme.error,
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
